import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faSignOutAlt, faQuestionCircle, faCog, faStore, faEllipsisVertical, faRankingStar, faGamepad, faGlobe, faCommentDots, faStar, faMobileScreen } from '@fortawesome/free-solid-svg-icons';
import { reopenCookieSettings } from './CookieConsent';
import { useAuth } from '../contexts/AuthContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useTranslation } from '../contexts/LanguageContext';
import { useMonetization } from '../contexts/MonetizationContext';
import { fetchPlayerRating } from '../services/api';
import { storeReviewUrl, storeUrlForThisDevice } from '../services/storeReview';
import { trackEvent } from '../utils/analytics';
import RankBadge from './RankBadge';
import LoginModal from './LoginModal';
import SettingsModal from './SettingsModal';
import LanguageSelector, { LanguageGrid } from './LanguageSelector';
import '../css/NavigationBar.css';

const NavigationBar = ({ onHelpClick }) => {
    const { isDarkMode, toggleDarkMode } = useDarkMode();
    const { user, isAuthenticated, logout, promptLogin, showLoginPrompt, dismissLoginPrompt } = useAuth();
    const { t } = useTranslation();
    const { streakData, openPurchaseModal, purchasesEnabled } = useMonetization();
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showOptionsMenu, setShowOptionsMenu] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [ratingData, setRatingData] = useState(null);
    const optionsRef = useRef(null);
    const location = useLocation();
    const isOnLeaderboard = location.pathname.includes('/leaderboard');
    const streak = streakData?.currentStreak;

    useEffect(() => {
        if (isAuthenticated) {
            fetchPlayerRating().then(res => {
                if (res?.success) setRatingData(res.rating);
            }).catch(() => {});
        }
    }, [isAuthenticated]);

    // Close options menu on click outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (optionsRef.current && !optionsRef.current.contains(e.target)) {
                setShowOptionsMenu(false);
            }
        };
        if (showOptionsMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showOptionsMenu]);

    const handleLoginClick = () => {
        promptLogin(t('nav.loginPrompt'));
    };

    const handleLogout = async () => {
        await logout();
        setShowUserMenu(false);
    };

    return (
        <>
            <nav className={`navbar ${isDarkMode ? 'dark-mode' : ''}`}>
                <div className="navbar-left">
                    <Link to="/" className="navbar-brand">
                        <img src="/logo192.png" alt="Word Labyrinth" className="brand-logo" />
                        <span className="brand-text">Word Labyrinth</span>
                    </Link>
                </div>

                <div className="navbar-right">
                    {isOnLeaderboard ? (
                        <Link
                            to="/"
                            className="leaderboard-button"
                            title={t('common.play')}
                            aria-label={t('common.play')}
                        >
                            <FontAwesomeIcon icon={faGamepad} />
                        </Link>
                    ) : (
                        <Link
                            to="/leaderboard"
                            className="leaderboard-button"
                            title={t('nav.leaderboard')}
                            aria-label={t('nav.leaderboard')}
                        >
                            <FontAwesomeIcon icon={faRankingStar} />
                        </Link>
                    )}

                    {purchasesEnabled && (
                        <button
                            className="shop-button"
                            onClick={openPurchaseModal}
                            title={t('nav.shop')}
                            aria-label={t('nav.shop')}
                        >
                            <FontAwesomeIcon icon={faStore} />
                        </button>
                    )}

                    {onHelpClick && (
                        <button
                            className="help-button desktop-only"
                            onClick={onHelpClick}
                            title={t('nav.howToPlay')}
                            aria-label={t('nav.howToPlay')}
                        >
                            <FontAwesomeIcon icon={faQuestionCircle} />
                        </button>
                    )}

                    <div className="desktop-only"><LanguageSelector /></div>

                    <button
                        type="button"
                        className={`theme-toggle desktop-only ${isDarkMode ? 'dark' : ''}`}
                        onClick={toggleDarkMode}
                        title={isDarkMode ? t('nav.switchToLight') : t('nav.switchToDark')}
                        aria-label={isDarkMode ? t('nav.switchToLight') : t('nav.switchToDark')}
                        aria-pressed={isDarkMode}
                    >
                        <span className="toggle-icon" aria-hidden="true">{isDarkMode ? '🌙' : '☀️'}</span>
                    </button>

                    {/* Options menu (visible on both desktop and mobile) */}
                    <div className="mobile-options-container" ref={optionsRef}>
                        <button
                            className="options-button"
                            onClick={() => {
                                setShowOptionsMenu(!showOptionsMenu);
                                setShowUserMenu(false);
                            }}
                            title={t('nav.options')}
                            aria-label={t('nav.options')}
                            aria-haspopup="true"
                            aria-expanded={showOptionsMenu}
                        >
                            <FontAwesomeIcon icon={faEllipsisVertical} />
                        </button>
                        {showOptionsMenu && (
                            <div className="options-dropdown">
                                <div className="options-dropdown-section mobile-menu-only">
                                    <div className="options-dropdown-label">
                                        <FontAwesomeIcon icon={faGlobe} />
                                        {t('nav.language')}
                                    </div>
                                    <LanguageGrid compact onSelect={() => setShowOptionsMenu(false)} />
                                </div>
                                <div className="options-dropdown-divider mobile-menu-only" />
                                {onHelpClick && (
                                    <button
                                        className="options-dropdown-item"
                                        onClick={() => { onHelpClick(); setShowOptionsMenu(false); }}
                                    >
                                        <FontAwesomeIcon icon={faQuestionCircle} />
                                        {t('nav.howToPlay')}
                                    </button>
                                )}
                                <button
                                    className="options-dropdown-item"
                                    onClick={() => { toggleDarkMode(); setShowOptionsMenu(false); }}
                                >
                                    <span>{isDarkMode ? '🌙' : '☀️'}</span>
                                    {isDarkMode ? t('nav.darkMode') : t('nav.lightMode')}
                                </button>
                                <button
                                    className="options-dropdown-item"
                                    onClick={() => { setShowSettings(true); setShowOptionsMenu(false); }}
                                >
                                    <FontAwesomeIcon icon={faCog} />
                                    {t('nav.settings')}
                                </button>
                                <button
                                    className="options-dropdown-item"
                                    onClick={() => { window.dispatchEvent(new CustomEvent('wl:open-feedback')); setShowOptionsMenu(false); }}
                                >
                                    <FontAwesomeIcon icon={faCommentDots} />
                                    {t('nav.sendFeedback')}
                                </button>
                                {(storeReviewUrl() || storeUrlForThisDevice()) && (
                                    <a
                                        className="options-dropdown-item"
                                        href={storeReviewUrl() || storeUrlForThisDevice()}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={() => { trackEvent(storeReviewUrl() ? 'Rate App Clicked' : 'Get App Clicked', { context: 'menu' }); setShowOptionsMenu(false); }}
                                    >
                                        <FontAwesomeIcon icon={storeReviewUrl() ? faStar : faMobileScreen} />
                                        {storeReviewUrl() ? t('nav.rateApp') : t('nav.getApp')}
                                    </a>
                                )}
                                <div className="options-dropdown-legal-footer">
                                    <div className="options-dropdown-legal-links">
                                        <Link to="/terms" onClick={() => setShowOptionsMenu(false)}>{t('nav.footer.terms')}</Link>
                                        <span aria-hidden="true">·</span>
                                        <Link to="/privacy" onClick={() => setShowOptionsMenu(false)}>{t('nav.footer.privacy')}</Link>
                                        <span aria-hidden="true">·</span>
                                        <button type="button" onClick={() => { reopenCookieSettings(); setShowOptionsMenu(false); }}>{t('nav.footer.cookies')}</button>
                                    </div>
                                    © {new Date().getFullYear()} CoreDL d.o.o.
                                </div>
                            </div>
                        )}
                    </div>

                    {isAuthenticated ? (
                        <div className="user-menu-container">
                            <button 
                                className="user-button"
                                onClick={() => { setShowUserMenu(!showUserMenu); setShowOptionsMenu(false); }}
                            >
                                <div className="user-avatar">
                                    {user?.username?.charAt(0).toUpperCase() || 'U'}
                                </div>
                                <span className="user-name">{user?.username}</span>
                            </button>

                            {showUserMenu && (
                                <div className="user-dropdown">
                                    <div className="dropdown-header">
                                        <span className="dropdown-email">{user?.email}</span>
                                    </div>
                                    {streak > 0 && (
                                        <div className="dropdown-streak">
                                            <span className="streak-fire">🔥</span> {t('nav.streak', { count: streak })}
                                            {streakData?.freezeCount > 0 && (
                                                <span className="streak-freezes"> · 🛡️ {streakData.freezeCount}</span>
                                            )}
                                        </div>
                                    )}
                                    {ratingData && ratingData.gamesRated > 0 && (
                                        <div className="dropdown-rating">
                                            <RankBadge
                                                tier={ratingData.rankTier}
                                                rating={ratingData.rating}
                                                size="small"
                                                provisional={ratingData.isProvisional}
                                            />
                                        </div>
                                    )}
                                    <button
                                        className="dropdown-item"
                                        onClick={() => {
                                            setShowSettings(true);
                                            setShowUserMenu(false);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faCog} />
                                        {t('nav.settings')}
                                    </button>
                                    <button
                                        className="dropdown-item logout"
                                        onClick={handleLogout}
                                    >
                                        <FontAwesomeIcon icon={faSignOutAlt} />
                                        {t('common.signOut')}
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <button className="login-button" onClick={handleLoginClick}>
                            <FontAwesomeIcon icon={faUser} />
                            <span>{t('common.signIn')}</span>
                        </button>
                    )}
                </div>
            </nav>

            {/* Login Modal */}
            {location.pathname !== '/' && (
                <LoginModal
                    show={showLoginPrompt}
                    onClose={dismissLoginPrompt}
                />
            )}

            {/* Settings Modal */}
            <SettingsModal
                show={showSettings}
                onClose={() => setShowSettings(false)}
            />
        </>
    );
};

export default NavigationBar;
